/* global TimeoutId */
import { notify } from 'app/services/errorReporter';
import DeviceManagerState, { IDeviceManagerBehaviorOptions } from 'app/services/rtc/DeviceManager/DeviceManagerState';
import { AutoAction } from 'app/services/rtc/DeviceManager/DeviceState';
import MediaDevices from 'app/services/rtc/MediaDevices';
import { WebRTCFeatures } from 'app/services/rtc/constants';
import { IValueTransformTask } from 'app/utils/queues/constants';
import { printDebugTree } from 'app/utils/debugTree';
import { bindDomEvents, unbindDomEvents } from 'app/utils/DomEvents';
import { EventHandlerMap } from 'app/utils/EventMapEmitter';
import SubscribableState, { makeVolatileValue, VolatileValue } from 'app/utils/SubscribableState';
import AsyncState from 'app/utils/queues/AsyncState';

export {
  AutoAction,
  AUTOMATIC_DEVICE_ID,
  DeviceInfo,
  DeviceState,
  DeviceType,
  UserAction,
} from 'app/services/rtc/DeviceManager/DeviceState';
export { default as TrackState } from 'app/services/rtc/TrackState';
export { DeviceManagerState };

const pollingTime = 1000;

// Prevent other modules from accessing service's state directly
const privateState = Symbol('DeviceManager');
export type DeviceManager = SubscribableState<{
  [privateState]: {
    asyncState: AsyncState<DeviceManagerState>;
    value: DeviceManagerState,
    isBusy: boolean,
    trackEventMap: EventHandlerMap<MediaStreamTrackEventMap>;
    mediaDevicesEventMap: EventHandlerMap<MediaDevicesEventMap>;
    pollTimeout: VolatileValue<TimeoutId>;
  },
}>;

export const createDeviceManager = (
  init = DeviceManagerState.init({}),
): DeviceManager => {
  printDebugTree('deviceManager', null, init);

  const onTracksStateChange = () => {
    updateDeviceManager(manager, state => state.updateTracksStates());
  };

  const onMediaDevicesChange = () => {
    updateDeviceManager(manager, async (state) => {
      const next = await state.updateAvailableDevices();

      if (next.autoAction === AutoAction.Watch) {
        return next.step();
      } else {
        return next;
      }
    });
  };

  const asyncState = new AsyncState({ init });

  // Update DeviceManager's state when the AsyncState's state changes.
  asyncState.subscribe(() => {
    manager.setState({
      [privateState]: {
        ...manager.state[privateState],
        value: asyncState.getValue(),
        isBusy: asyncState.isBusy(),
      },
    });
  });

  const manager: DeviceManager = new SubscribableState({
    [privateState]: {
      asyncState,
      value: asyncState.getValue(),
      isBusy: asyncState.isBusy(),
      trackEventMap: {
        mute:   onTracksStateChange,
        unmute: onTracksStateChange,
        ended:  onTracksStateChange,
      },
      mediaDevicesEventMap: {
        devicechange: onMediaDevicesChange,
      },
      pollTimeout: makeVolatileValue<TimeoutId>(-1),
    },
  });

  return manager;
};

export const getIsDeviceManagerBusy = (manager: DeviceManager | undefined) => (
  manager?.state[privateState].isBusy ?? false
);

export const getDeviceManagerState = (manager: DeviceManager | undefined) => (
  manager?.state[privateState].value ?? DeviceManagerState.init({})
);

export const startDeviceManager = (manager: DeviceManager, options: IDeviceManagerBehaviorOptions) => {
  if (!MediaDevices.getSupportedFeatures().has(WebRTCFeatures.UserMedia)) {
    return;
  }

  updateDeviceManager(manager, (state) => {
    if (state.audio.isInitial) {
      MediaDevices.bindEvents(manager.state[privateState].mediaDevicesEventMap);
    }

    return state.start(options);
  });
};

export const stopDeviceManager = (manager: DeviceManager) => {
  if (!MediaDevices.getSupportedFeatures().has(WebRTCFeatures.UserMedia)) {
    return;
  }

  const { mediaDevicesEventMap, pollTimeout } = manager.state[privateState];

  clearTimeout(pollTimeout.get());
  updateDeviceManager(manager, (state) => {
    MediaDevices.unbindEvents(mediaDevicesEventMap);

    return state.stop();
  });
};

export const updateDeviceManager = async (
  manager: DeviceManager,
  action: IValueTransformTask<DeviceManagerState>,
) => {
  const { trackEventMap, pollTimeout, asyncState } = manager.state[privateState];

  const subscribeTrackEvents = (state: DeviceManagerState) => {
    [
      state.audio.stream,
      state.video.stream,
      state.screen.stream,
    ].forEach((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          bindDomEvents(track, trackEventMap);
        });
      }
    });
  };

  const unsubscribeTrackEvents = (state: DeviceManagerState) => {
    [
      state.audio.stream,
      state.video.stream,
      state.screen.stream,
    ].forEach((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          unbindDomEvents(track, trackEventMap);
        });
      }
    });
  };

  const poll = () => {
    clearTimeout(pollTimeout.get());
    pollTimeout.set(setTimeout(() => pollAction(), pollingTime));
  };

  const pollAction = () => {
    updateDeviceManager(manager, async (state) => {
      if (state.autoAction === AutoAction.Poll) {
        return await state.step();
      } else {
        return state;
      }
    });
  };

  asyncState.scheduleUpdate(async (state, cancelToken) => {
    try {
      const next = await action(state, cancelToken);

      if (next !== state) {
        printDebugTree('deviceManager', state, next);

        if (state) unsubscribeTrackEvents(state);
        if (next) subscribeTrackEvents(next);
      }

      if (next.autoAction === AutoAction.Poll) {
        poll();
      }

      return next;
    } catch (error) {
      notify(error, { name: 'DeviceManager' });
      return state;
    }
  });
};
